import { useId, useState, type CSSProperties, type ReactNode } from "react";

export type CollapsibleSectionProps = {
  title: string;
  /** One-line status shown when collapsed (Level 1–2). */
  summary?: ReactNode;
  /** When true, section starts open. Default false. */
  defaultOpen?: boolean;
  /** Controlled open state — when set, ignores internal state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Accessible name override for the toggle. */
  toggleLabel?: string;
};

/**
 * Progressive-disclosure group for Ops / Control Plane chrome.
 * Collapsed = operator summary; expanded = details or diagnostics.
 */
export function CollapsibleSection({
  title,
  summary,
  defaultOpen = false,
  open: openControlled,
  onOpenChange,
  children,
  className = "",
  style,
  toggleLabel,
}: CollapsibleSectionProps): ReactNode {
  const reactId = useId();
  const panelId = `ops-collapse-${reactId}`;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = openControlled ?? uncontrolledOpen;

  const setOpen = (next: boolean) => {
    if (openControlled === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <section
      className={`ops-collapse ${open ? "ops-collapse--open" : ""} ${className}`.trim()}
      style={style}
      data-open={open ? "true" : "false"}
    >
      <button
        type="button"
        className="ops-collapse__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          width: "100%",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.75rem",
          margin: 0,
          padding: "0.35rem 0",
          border: "none",
          background: "transparent",
          color: "inherit",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ minWidth: 0, flex: 1 }}>
          <span
            style={{
              display: "block",
              fontSize: "0.7rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >
            <span aria-hidden="true" style={{ marginRight: "0.35rem", color: "#64748b" }}>
              {open ? "▼" : "▶"}
            </span>
            {title}
          </span>
          {!open && summary ? (
            <span
              style={{
                display: "block",
                marginTop: "0.25rem",
                fontSize: "0.8rem",
                color: "#cbd5e1",
                lineHeight: 1.35,
              }}
            >
              {summary}
            </span>
          ) : null}
        </span>
        <span style={{ fontSize: "0.68rem", color: "#93c5fd", whiteSpace: "nowrap" }}>
          {toggleLabel ?? (open ? "Hide" : "Show")}
        </span>
      </button>
      {open ? (
        <div id={panelId} className="ops-collapse__body" style={{ marginTop: "0.35rem" }}>
          {children}
        </div>
      ) : null}
    </section>
  );
}
